import { useEffect } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { QueryKey } from '@tanstack/react-query';
import api, { API_BASE } from '../services/api';
import { queryClient } from './queryClient';

/**
 * Live updates from the server: one Server-Sent Events stream per signed-in tab (backend:
 * routes/realtime_routes.py). Each event names the API resource that changed -- by any user in the tenant, or
 * by a background scan -- and the matching cached queries are refetched. Query keys start with that resource
 * name (see lib/queries.ts), so mapping an event to the screens that show it needs no per-screen wiring.
 *
 * fetch-event-source is used instead of the browser's EventSource because it can send the Authorization
 * header (EventSource can't, and putting the token in the URL would leak it into logs).
 */

interface ChangeEvent {
  resource: string;
  id: string | null;
  action: string;
}

// What else is shown alongside a resource and goes stale when it changes.
const SCAN_EFFECTS = ['vendors', 'vendor-requests', 'dashboard', 'reports', 'monitoring', 'notifications', 'history', 'my-risk'];
const RELATED: Record<string, string[]> = {
  scan: ['scans', ...SCAN_EFFECTS],
  scans: ['scan', ...SCAN_EFFECTS],
  vendors: ['dashboard', 'reports', 'monitoring', 'vendor-requests', 'history', 'scan', 'my-risk'],
  // My Risk is a vendor row underneath, so its own changes refresh the vendor views too.
  'my-risk': ['vendors'],
  'vendor-requests': ['vendors', 'dashboard'],
  'triage-suggestions': ['dashboard'],
  monitoring: ['notifications', 'vendors', 'dashboard'],
  users: ['vendors'],
  // Sent questionnaires are shown on the vendor's Questionnaires tab.
  'questionnaire-responses': ['vendors'],
  // Vendor replies/submissions and scan progress on a request also raise a tenant notification.
  remediation: ['notifications', 'dashboard'],
};
// Every write is recorded in the audit log, so it is stale after any change.
const ALWAYS = ['audit'];

function invalidateFor(event: ChangeEvent) {
  const roots = new Set([event.resource, ...(RELATED[event.resource] ?? []), ...ALWAYS]);
  roots.forEach(root => queryClient.invalidateQueries({ queryKey: [root] as QueryKey }));
}

class StopStream extends Error {}

const RETRY_BASE_MS = 2_000;
const RETRY_MAX_MS = 30_000;

export function useRealtimeSync(userId: string | undefined) {
  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();
    let opened = false;
    let failures = 0;

    fetchEventSource(`${API_BASE}/v1/events/stream`, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` },
      // Default behaviour: close while the tab is hidden, reconnect when it's visible again.
      async onopen(response) {
        if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
          // After any gap (reconnect, tab was hidden) events may have been missed -- resync what's on screen.
          if (opened) queryClient.invalidateQueries();
          opened = true;
          failures = 0;
          return;
        }
        if (response.status === 401 || response.status === 403) throw new StopStream();
        throw new Error(`Realtime stream unavailable (${response.status})`);
      },
      onmessage(message) {
        if (!message.data || message.event === 'ready') return;
        try {
          const event = JSON.parse(message.data) as ChangeEvent;
          // An admin ended sessions of this user: ask the server whether this tab's session is one of them.
          // If it is, the 401 signs this tab out (services/api.ts); if not, nothing happens.
          if (event.resource === 'session-revoked') {
            if (event.id === userId) api.get('/v1/auth/me').catch(() => {});
            return;
          }
          invalidateFor(event);
        } catch {
          /* ignore a malformed event; the next refetch on focus covers it */
        }
      },
      onerror(error) {
        if (error instanceof StopStream) throw error;
        failures += 1;
        return Math.min(RETRY_BASE_MS * 2 ** (failures - 1), RETRY_MAX_MS);
      },
    }).catch(() => {
      /* stopped: signed out or no longer authorized */
    });

    return () => controller.abort();
  }, [userId]);
}
